import { Box, CircularProgress } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import Typography from '@ui/Typography';
import { useActorsWithFiltersQuery } from '@avatar/graphql/filterAvatars/operation.generated';
import type { Actor } from '@avatar/types';
import parseApolloError from '../../../../util/apolloError';
import ActorsList, { ActorsListProps, AvatarWithVariant } from './ActorsList';
import {
  actorContainsNonArchivedVariant,
  sortAvatarsByClipOrder,
} from '../../utils/actor';
import useStudioClips from '../../../StudioClipUserList/hooks/useStudioClips';
import { useUserSubscription } from '../../../Account/operation.generated';
import usePrevious from '../../../../lib/usePrevious';
import {
  ActorVariant,
  AvatarOrderByOrder,
} from '../../../../../server/graphql/__generated.types';
import { ProjectFragment } from '../../../fragments.generated';

export interface Avatar
  extends Pick<
    Actor,
    | 'id'
    | 'name'
    | 'initials'
    | 'image'
    | 'preview'
    | 'accessLevel'
    | 'availableIn'
    | 'isCustomActor'
    | 'variants'
  > {}

export interface AvatarVariant extends ActorVariant {}

export interface ActorsProps
  extends Pick<
    ActorsListProps,
    'activeActor' | 'setActiveActor' | 'limit' | 'activeActorVariant' | 'tags'
  > {
  project: ProjectFragment;
}

/**
 * Renders list of avatars in studio editor right rail (script mode)
 */
export default function Actors({
  activeActor,
  activeActorVariant,
  setActiveActor,
  project,
  limit,
  tags,
}: ActorsProps) {
  const projectId = project.id;
  const { error, loading, data, refetch } = useActorsWithFiltersQuery({
    variables: {
      inputs: {
        projectVersion: project.studioVersion,
        orderBy: AvatarOrderByOrder.NameAsc,
      },
    },
  });
  const [{ clips, variables }] = useStudioClips(projectId);
  const { data: subscriptionData } = useUserSubscription();
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState<
    string | null
  >(null);

  // this bit of logic keeps the avatar list from being affected by search in order to prevent
  // flickering. This could be much simpler, unfortunately, `variables` from the clips' query
  // renders once the previous clips, and then with the correct clip list response. For this reason
  // we have to rely on `usePrevious` and `useState`
  const searchTerm = variables?.clipsInputs.search;
  const previousClips = usePrevious(clips);
  const [clipsForAvatarsList, setClipsForAvatarsList] = useState(
    !searchTerm ? clips : null
  );
  useEffect(() => {
    // we only care about keeping the list of clips if we don't have a search term (full list)
    if (searchTerm) return;
    // Only update if the clips have changed. Using `isEqual`, since clips and previousClips won't
    // have referencial equality.
    if (!isEqual(clips, previousClips)) setClipsForAvatarsList(clips);
  }, [clips, previousClips, searchTerm]);

  // Whenever user actors query changes, update our local state with sorting and filtering
  // NOTE: we update actor ordering when `clips` size changes as well, but we only have a subset
  // of the clips available on the client.
  const actors = useMemo(() => {
    const availableActors = data ? data.user.actors : [];
    return sortAvatarsByClipOrder(
      projectId,
      availableActors,
      clipsForAvatarsList ?? []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    projectId,
    // clips included here to have the latests ref data from `clipsForAvatarsList`
    clips,
  ]);

  const recentActorVariants: Array<AvatarWithVariant> = useMemo(() => {
    const availableActors = data ? data.user.actors : [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      clipsForAvatarsList
        ?.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
        .filter(c => c.projectId === projectId)
        .reduce((acc, clip) => {
          const avatar: Avatar | undefined = availableActors.find(
            a => a.id === Number(clip.actorId)
          );
          const variant: AvatarVariant | undefined = avatar?.variants.find(
            v => v.id === clip.actor.variant.id
          );

          if (avatar && variant) {
            if (
              acc.some(
                av =>
                  av.avatar?.id === avatar.id && av.variant?.id === variant.id
              )
            ) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              return acc;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return acc.concat([
              {
                avatar: avatar!,
                variant: variant!,
              },
            ]);
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return acc;
        }, [] as Array<AvatarWithVariant>) ?? []
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    projectId,
    // clips included here to have the latests ref data from `clipsForAvatarsList`
    clips,
    clipsForAvatarsList,
  ]);

  // Once actors have loaded, set the active actor
  useEffect(() => {
    if (
      (!activeActor || !actorContainsNonArchivedVariant(activeActor)) &&
      recentActorVariants.length > 0
    ) {
      setActiveActor(
        recentActorVariants[0].avatar,
        recentActorVariants[0].variant
      );
    }
  }, [recentActorVariants, setActiveActor, activeActor]);

  const subscriptionId = useMemo(() => {
    return subscriptionData?.user.subscription?.plan.id || '';
  }, [subscriptionData]);

  useEffect(() => {
    setCurrentSubscriptionId(subscriptionId);
  }, [subscriptionId]);

  // Update actors list when user subscription changes
  useEffect(() => {
    if (
      subscriptionId &&
      currentSubscriptionId &&
      subscriptionId !== currentSubscriptionId
    ) {
      refetch({
        inputs: {
          projectVersion: project.studioVersion,
        },
      }).then(() => {
        setCurrentSubscriptionId(subscriptionId);
      });
    }
  }, [refetch, subscriptionId, currentSubscriptionId, project.studioVersion]);

  if (!data && loading) {
    return (
      <Box display="flex" justifyContent="center" marginTop={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data && error) {
    return (
      <Typography variant="pSm" role="alert" color="error">
        {parseApolloError(
          error,
          'Error loading actors list, please reload your browser'
        )}
      </Typography>
    );
  }

  return (
    <ActorsList
      actors={actors}
      activeActor={activeActor}
      activeActorVariant={activeActorVariant}
      setActiveActor={setActiveActor}
      limit={limit}
      recentAvatarVariants={recentActorVariants}
      projectVersion={project.studioVersion}
      tags={tags}
    />
  );
}
